import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { connect } from 'react-redux';
import getConfig from 'next/config';
import {
  Tag as BaseTag,
  Button,
  // $FlowFixMe
} from '@bijie/react-bnb';
import {
  formatNumber,
  formatNumberForIntegralPart,
} from '../../../utils/number';
import { FlexBox } from '../../FlexBox';
import { RateSubTitle, STAKE_STATUS } from '../constants';
import {
  getLabelWithDefaultStr,
} from '../../../utils/i18n';
import { getRemainDayStr } from '../utils';
import type { StakeModel } from '../types';
import { COLORS } from '../styles';
import { Link } from '../../../routes';
import withI18n from '../../../utils/withI18n';

const {
  publicRuntimeConfig: {
    assetPrefix,
  },
} = getConfig();

const lightTheme = {
  _borderColor: '#EAECEF',
  _tagFontColor: '#848E9C',
  _fieldColor: '#848E9C',
  _valueColor: '#48515D',
  _titleColor: '#212833',
  _hrColor: 'rgba(234,236,239, 0.6)',
  _hoverStyle: 'box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.04)',
  _background: 'white',
};

const darkTheme = {
  _borderColor: 'rgba(234, 236, 239, 0.2)',
  _tagFontColor: 'rgba(255, 255, 255, 0.6)',
  _fieldColor: 'rgba(255, 255, 255, 0.4)',
  _valueColor: 'white',
  _titleColor: 'white',
  _hrColor: 'rgba(234,236,239, 0.1)',
  _hoverStyle: 'background: rgba(255, 255, 255, 0.05)',
  _background: '#12161C',
};

const Container = styled.div`  
  width: 616px;
  margin: 12px;
  padding: 32px;
  border: 1px solid ${({ theme }) => theme._borderColor};
  border-radius: 10px;
  cursor: pointer;
  background-color: ${({ theme }) => theme._background};
  
  &:hover {
    ${({ theme }) => theme._hoverStyle};
  }
  
  &:nth-child(odd) {
    margin-left: 0px;
  }
  
  &:nth-child(even) {
    margin-right: 0px;
  }
`;

const Field = styled.div`
  font-size: ${props => (props.small ? '12px' : '14px')};
  color: ${({ theme, alert }) => (alert && COLORS.red) || theme._fieldColor};
  text-align: ${(props) => (props.textAlign ? props.textAlign : 'left')};
`;

const Value = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme._valueColor};
`;

const FirstRow = FlexBox;

const SecondRow = styled.div`
  padding: 24px 66px;
  font-size: 14px;
`;

const Hr = styled.div`
  height: 1px;
  background: ${({ theme }) => theme._hrColor};;
`;

const ThirdRow = styled(FlexBox)`
  padding: 24px 0 0 66px;
  justify-content: space-between;
  align-items: center; 
`;

const CoinLogo = styled.div`
  height: 50px;
  width: 50px;
  border-radius: 25px;
  border: 1px solid ${({ theme }) => theme._borderColor};
  display: flex;
  justify-content: center;
  align-items: center;
  
  img {
    width: 80%;
    height: 80%;
  }
`;

const RateRoughRange = styled.div`
  display: flex;
  align-items: flex-end;
  .rate {
    font-size: 34px;
    line-height: 34px;
    color: ${({ theme }) => theme._titleColor};
    font-weight: 400;
  }
  .dash {
      color: ${({ theme }) => theme._titleColor};
      font-size: 30px;
      font-weight: 400;
      margin-right: 3px;
      line-height: 34px;
  }
  .percentage {
     font-size: 12px;
     color: ${({ theme }) => theme._titleColor};
  }
`;

const ReturnRateDiv = styled.div`
  max-width: 130px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
`;

const TitleDiv = styled.div`
  flex: 1;
  padding: 0 16px;
`;

const Title = styled.div`
  font-size: 20px;
  line-height: 28px;
  color: ${({ theme }) => theme._titleColor};
`;

const Rate = styled.div`
  font-size: 34px;
  line-height: 34px;
  color: ${({ theme, green }) => green ? theme.up : theme._titleColor};
  font-weight: 400;
  position: relative;
  margin-right: 12px;
  
  &:after {
    content: '%';
    visibility: ${({ noShowPercentage }) => noShowPercentage ? 'hidden' : 'visible'};
    position: absolute;
    height: 12px;
    bottom: 15px;
    right: -12px;
    font-size: 12px;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Tag = styled(BaseTag)`
  margin: 5px;
  color: ${({ theme }) => theme._tagFontColor};
  border-radius: 16px;
  padding: 1px 11px;
  font-size: 12px;
  
  &:first-child {
    margin-left: 0;
  }
`;

const AlignField = styled.div`
  height: 21px;
`;

type ReturnRateProps = {
  status: string,
  returnOfRate: string,
  lastEarningRate: string,
  i18n: Object,
};

const ReturnRate = ({
  status,
  returnOfRate,
  lastEarningRate,
  i18n,
}: ReturnRateProps) => {
  let rate;

  switch (status) {
    case STAKE_STATUS.upComing: {
      const [bound1, bound2] = returnOfRate.split(';');
      const [upperBound, lowerBound] = Number(bound2) > Number(bound1) ? [bound2, bound1] : [bound1, bound2];
      rate = (
        <RateRoughRange>
          <span className="rate">{lowerBound}</span>
          <span className="percentage">%</span>
          <span className="dash">-</span>
          <span className="rate">{upperBound}</span>
          <span className="percentage">%</span>
        </RateRoughRange>
      );
      break;
    }
    case STAKE_STATUS.inComing:
    case STAKE_STATUS.ended:
    default: {
      rate = (
        <Rate
          green={status === STAKE_STATUS.inComing}
          fullWidth={true}
          noShowPercentage={!lastEarningRate}
        >
          {lastEarningRate || '--'}
        </Rate>
      );
      break;
    }
  }
  return (
    <ReturnRateDiv>
      <FlexBox>
        {rate}
      </FlexBox>
      <Field
        textAlign="center"
        small={true}
      >
        {RateSubTitle(i18n)(status)}
      </Field>
    </ReturnRateDiv>
  );
};

type ContainerProps = {
  isLoggedIn: boolean,
}

const mapStateToProps = ({ isLoggedIn }) => ({ isLoggedIn });

export default
@connect(mapStateToProps)
@withI18n
class CardContainer extends React.PureComponent<ContainerProps> {
  render() {
    return (
      <Card
        {...this.props}
      />
    );
  }
}

type CardProps = {
  i18n: Object,
  locale: string,
  inverted: boolean,
  isLoggedIn: boolean,
  onClick: Function,
  ...StakeModel,
}

const Card = ({
  i18n,
  locale = 'en',
  inverted = false,
  isLoggedIn,
  onClick = () => {},
  stakingName = '-------',
  returnOfRate = '0;0',
  leastHoldCoin = '0',
  lastEarningRate = '0.0',
  thirtyDaysHoldCoin = '0',
  holdCoin = 'XXX',
  rebateCoin = 'XXX',
  startTime = '1970-01-01',
  status = STAKE_STATUS.upComing,
  tags,
  totalRebateCoin = '0',
}: CardProps) => {
  function returnRateStrHelper(returnRate) {
    const rates = returnRate.split(';');
    return `${rates[0]}%-${rates[1]}%`;
  }

  const returnRateStr = returnRateStrHelper(returnOfRate);
  const logoUrl = `${assetPrefix}/static/images/coin/${holdCoin.toLowerCase()}.svg`;
  const onLandingPage = inverted;
  const remainDayStr = getRemainDayStr(startTime, i18n);

  return (
    <ThemeProvider
      theme={theme => ({
        ...theme,
        ...(inverted ? darkTheme : lightTheme),
      })}
    >
      <Container
        onClick={onClick}
      >
        <FirstRow>
          <CoinLogo>
            <img
              src={logoUrl}
              alt="coin"
            />
          </CoinLogo>
          <TitleDiv>
            <Title>{stakingName}</Title>
            {tags && (
              <Tags>
                {tags.split(';').map(tag => (
                  <Tag
                    key={tag}
                  >
                    {tag}
                  </Tag>
                ))}
              </Tags>
            )}
          </TitleDiv>
          <ReturnRate
            i18n={i18n}
            status={status}
            returnOfRate={returnOfRate}
            lastEarningRate={lastEarningRate}
          />
        </FirstRow>
        <SecondRow>
          {
            (status === STAKE_STATUS.upComing && onLandingPage) && (
              <AlignField />
            )
          }
          {
            (status === STAKE_STATUS.inComing) && (
              <Field>
                {
                  getLabelWithDefaultStr(
                    'exchange-staking-return-rate',
                    i18n,
                    '参考年回报率')
                }:&nbsp;
                <Value>{returnRateStr}</Value>
              </Field>
            )
          }
          {
            (status === STAKE_STATUS.inComing || status === STAKE_STATUS.upComing) && (
              <Field>
                {
                  getLabelWithDefaultStr(
                    'exchange-staking-least-hold-coin',
                    i18n,
                    '每日最低持仓')
                }:&nbsp;
                <Value>{formatNumberForIntegralPart(leastHoldCoin)}&nbsp;{holdCoin}</Value>
              </Field>
            )
          }
          {
            (status === STAKE_STATUS.ended) && (
              <Field>
                {
                  getLabelWithDefaultStr(
                    'exchange-staking-total-rebate',
                    i18n,
                    '累计返利')
                }:&nbsp;
                <Value>{formatNumber(totalRebateCoin)}&nbsp;{rebateCoin}</Value>
              </Field>
            )
          }
        </SecondRow>
        {
          (status !== STAKE_STATUS.ended) && (
            <>
              <Hr />
              <ThirdRow>
                <div>
                  {status === STAKE_STATUS.inComing ?
                    isLoggedIn && (
                      <Field>
                        {getLabelWithDefaultStr(
                          'exchange-staking-30-days-hold-coin',
                          i18n,
                          '我的近30天累计持仓',
                        )}
                        :&nbsp;
                        <Value>
                          {formatNumber(thirtyDaysHoldCoin, 8)}&nbsp;{holdCoin}
                        </Value>
                      </Field>
                    ) : (
                      <Field
                        alert={true}
                      >
                        {remainDayStr}
                      </Field>
                    )}
                </div>
                <Link
                  route={`/${locale}/usercenter/wallet/deposit/${holdCoin}`}
                >
                  <Button
                    type="primary"
                    size="lg"
                  >
                    {getLabelWithDefaultStr(
                      'exchange-staking-deposit',
                      i18n,
                      '马上充值')}
                  </Button>
                </Link>
              </ThirdRow>
            </>
          )
        }
      </Container>
    </ThemeProvider>
  );
};
